import { Injectable } from "@angular/core";
import {
  Area,
  Assertion,
  AssertionIssue,
  Component as JobComponent
} from "../../../core/database/db";
import { InspectionSearchItem } from "../../../personas/ccm/BQ7/global-inspection-search/inspection-item-search.component";

export interface InspectionSearchArea extends Area {
  components: JobComponent[];
  assertions: Assertion[];
  issues: AssertionIssue[];
  isCcmInspection: boolean;
}

@Injectable({
  providedIn: "root"
})
export class AreaInspectionSearchService {
  buildFromAreaDetails(areas: InspectionSearchArea[]): InspectionSearchItem[] {
    return areas
      .filter(area => !!area.id)
      .flatMap(area =>
        this.buildFromFlatData(
          [area],
          area.components,
          area.assertions,
          area.issues,
          area.isCcmInspection
        )
      );
  }

  buildFromFlatData(
    areas: Area[],
    components: JobComponent[],
    assertions: Assertion[],
    assertionIssues: AssertionIssue[],
    isCcmInspection: boolean
  ): InspectionSearchItem[] {
    const areaById = new Map(areas.map(area => [area.id, area]));
    const componentById = new Map(components.map(component => [component.id, component]));

    const issuesByAssertionIdentifier = assertionIssues.reduce((dict, issue) => {
      if (!dict[issue.assertionIdentifier]) {
        dict[issue.assertionIdentifier] = [];
      }

      dict[issue.assertionIdentifier].push(issue);
      return dict;
    }, {} as { [assertionIdentifier: string]: AssertionIssue[] });

    return assertions
      .filter(assertion => !!assertion.identifier)
      .reduce((items, assertion) => {
        const component = componentById.get(assertion.componentId);

        if (!component) {
          return items;
        }

        const area = areaById.get(component.areaId);

        if (!!area?.id === false) {
          return items;
        }

        items.push({
          id: `${area.id}-${assertion.identifier}`,
          areaId: area.id,
          areaName: area.name,
          categoryName: component.name ?? "Uncategorized",
          assertionIdentifier: assertion.identifier,
          assertionText: assertion.text ?? "",
          isReviewed: this.isAssertionReviewed(
            assertion,
            issuesByAssertionIdentifier[assertion.identifier] ?? [],
            isCcmInspection
          ),
        });

        return items;
      }, [] as InspectionSearchItem[]);
  }

  private isAssertionReviewed(assertion: Assertion, assertionIssues: AssertionIssue[], isCcmInspection: boolean): boolean {
    return (isCcmInspection ? assertion.isCcmComplete : assertion.isComplete)
      || !assertion.isApplicable
      || assertionIssues.length > 0;
  }
}